import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe, ChevronDown, MessageCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const languageNames = {
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano'
};

export const Navbar = () => {
  const { t, language, changeLanguage, availableLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: t('nav_home') },
    { path: '/#services', label: t('nav_services') },
    { path: '/#fleet', label: t('nav_fleet') },
    { path: '/booking', label: t('nav_booking') },
    { path: '/contact', label: t('nav_contact') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('#')[0]);
  };

  const handleNavClick = (path) => {
    setIsOpen(false);
    if (path.includes('#')) {
      const [, hash] = path.split('#');
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <div className="flex flex-col">
              <span className="text-xl font-semibold tracking-tight gold-text" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                EXCLUSIVE TOUR
              </span>
              <span className="text-xs tracking-[0.3em] text-[#A1A1AA] uppercase">
                Transfer
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className={`text-sm font-medium transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-[#D4AF37]'
                    : 'text-white/80 hover:text-[#D4AF37]'
                }`}
                data-testid={`nav-link-${link.path.replace(/[/#]/g, '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Switcher & Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:text-[#D4AF37] transition-colors" data-testid="language-switcher">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{languageNames[language]}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121212] border-white/10">
                {availableLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang}
                    onClick={() => changeLanguage(lang)}
                    className={`cursor-pointer ${language === lang ? 'text-[#D4AF37]' : 'text-white'}`}
                    data-testid={`language-option-${lang}`}
                  >
                    {languageNames[lang]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* WhatsApp */}
            <a
              href="https://wa.me/41799270731"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 px-4 py-2 text-green-500 hover:text-green-400 transition-colors"
              data-testid="navbar-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
            </a>

            {/* Book Now CTA */}
            <Link
              to="/booking"
              className="hidden sm:block px-5 py-2.5 bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#C5A059] transition-all duration-300"
              data-testid="navbar-book-now"
            >
              {t('nav_booking')}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/10" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-base font-medium py-2 ${
                    isActive(link.path) ? 'text-[#D4AF37]' : 'text-white/80'
                  }`}
                  data-testid={`mobile-nav-link-${link.path.replace(/[/#]/g, '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/booking"
                className="mt-2 py-3 bg-[#D4AF37] text-black text-center font-semibold"
                onClick={() => setIsOpen(false)}
                data-testid="mobile-book-now"
              >
                {t('nav_booking')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
